import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  updateDoc,
  where
} from 'firebase/firestore';
import { auth, db } from './firebase';
import { checkIsFollowing, followUser } from './mediaService';
import type { AppUser } from '../types';

const RETRY_DELAYS_MS = [300, 900, 2000] as const;

export interface OfficialAccountTarget {
  uid: string;
  displayName?: string;
}

interface OfficialFollowDependencies {
  isFollowing: (followerUid: string, targetUid: string) => Promise<boolean>;
  follow: (
    targetUid: string,
    user: AppUser,
    targetName: string,
    options: { silent: true }
  ) => Promise<boolean>;
  isCurrentSession: () => boolean;
  retry?: <T>(operation: () => Promise<T>) => Promise<T>;
}

const wait = (milliseconds: number): Promise<void> =>
  new Promise(resolve => window.setTimeout(resolve, milliseconds));

const isRetryableFollowError = (error: unknown): boolean => {
  const code = typeof error === 'object' && error !== null && 'code' in error
    ? String((error as { code: unknown }).code)
    : '';
  const normalizedCode = code.split('/').pop();
  return normalizedCode === 'permission-denied' || normalizedCode === 'aborted';
};

export async function retryOfficialFollow<T>(
  operation: () => Promise<T>,
  sleep: (milliseconds: number) => Promise<void> = wait,
  random: () => number = Math.random
): Promise<T> {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await operation();
    } catch (error) {
      if (!isRetryableFollowError(error) || attempt >= RETRY_DELAYS_MS.length) throw error;
      const baseDelay = RETRY_DELAYS_MS[attempt];
      const jitteredDelay = Math.round(baseDelay * (0.8 + random() * 0.4));
      await sleep(jitteredDelay);
    }
  }
}

export async function followOfficialAccountsSequentially(
  user: AppUser,
  accounts: OfficialAccountTarget[],
  dependencies: OfficialFollowDependencies
): Promise<void> {
  const retry = dependencies.retry || retryOfficialFollow;
  for (const account of accounts) {
    if (!dependencies.isCurrentSession()) return;
    if (!account.uid || account.uid === user.uid) continue;

    try {
      await retry(async () => {
        if (await dependencies.isFollowing(user.uid, account.uid)) return;
        await dependencies.follow(account.uid, user, account.displayName || 'Official account', { silent: true });
      });
    } catch (error) {
      console.error(`Could not auto-follow official account ${account.uid}; continuing with the next account:`, error);
    }
  }
}

export async function seedOfficialFollowsForUser(
  user: AppUser,
  isCurrentSession: () => boolean = () => auth.currentUser?.uid === user.uid
): Promise<void> {
  if (!user.uid || !isCurrentSession()) return;

  const userRef = doc(db, 'users', user.uid);
  const userSnapshot = await getDoc(userRef);
  if (!userSnapshot.exists()) {
    throw new Error(`Cannot seed official follows before users/${user.uid} exists.`);
  }
  if (userSnapshot.get('officialFollowSeededAt')) return;

  const officialSnapshot = await getDocs(query(
    collection(db, 'officialAccounts'),
    where('active', '==', true),
    limit(20)
  ));

  await followOfficialAccountsSequentially(user, officialSnapshot.docs.map(officialDoc => {
    const account = officialDoc.data();
    return {
      uid: typeof account.uid === 'string' ? account.uid : '',
      displayName: typeof account.displayName === 'string' ? account.displayName : undefined
    };
  }), {
    isFollowing: checkIsFollowing,
    follow: followUser,
    isCurrentSession
  });

  if (!isCurrentSession()) return;
  await updateDoc(userRef, { officialFollowSeededAt: serverTimestamp() });
}
